import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowRight } from "@phosphor-icons/react";
import { builderNotes, gameCopy, notesCopy } from "../../content";
import { useGame } from "../../game/GameContext";
import { Waypoint } from "../game/Waypoint";
import { Button } from "../ui/Button";

const EASE = [0.16, 1, 0.3, 1] as const;

/** A minimal line-art paper clip, drawn in the chart style, holding the note. */
function PaperClip({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 46" className={className} aria-hidden focusable="false">
      <path
        d="M6 33V10a6 6 0 0 1 12 0v27a4 4 0 0 1-8 0V14a2 2 0 0 1 4 0v20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/**
 * Interaction 03: one builder note at a time, cycled by hand.
 * builderNotes are fictional placeholder material until real community
 * submissions replace them (see content.ts).
 */
export function SameBoatNotes() {
  const reduce = useReducedMotion();
  const { stamp } = useGame();
  const [index, setIndex] = useState(0);

  const reveal = (delay: number) => ({
    initial: reduce ? false : { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.65, delay, ease: EASE },
  });

  /* Reduced motion swaps notes with a plain crossfade; otherwise the note
   * slides and settles into its pinned tilt. */
  const swap = reduce
    ? {
        initial: { opacity: 0, rotate: -1.5 },
        animate: { opacity: 1, rotate: -1.5 },
        exit: { opacity: 0, rotate: -1.5 },
        transition: { duration: 0.25 },
      }
    : {
        initial: { opacity: 0, x: 36, rotate: 1 },
        animate: { opacity: 1, x: 0, rotate: -1.5 },
        exit: { opacity: 0, x: -36, rotate: -4 },
        transition: { duration: 0.45, ease: EASE },
      };

  return (
    <section id="notes" className="bg-paper-deep py-24 md:py-36">
      <div className="mx-auto w-full max-w-[1400px] px-5 md:px-10">
        <div className="mx-auto flex w-full max-w-xl flex-col items-center">
          <motion.div {...reveal(0)} className="w-full">
            <Waypoint
              station="notes"
              title={notesCopy.title}
              hook={gameCopy.stations.notes.hook}
              align="center"
            />
          </motion.div>

          {/* min-h on both the stage and the card keeps the column steady
           * while AnimatePresence swaps notes of different lengths. */}
          <motion.div
            {...reveal(0.08)}
            aria-live="polite"
            className="relative mt-12 min-h-[12rem] w-full"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={index}
                {...swap}
                className="relative flex min-h-[12rem] w-full items-center rounded-md border-2 border-ink bg-manila p-8 shadow-card md:p-10"
              >
                <PaperClip className="absolute -top-5 left-10 h-11 w-6 rotate-[5deg] text-ink" />
                <p className="text-xl leading-snug font-medium text-ink md:text-2xl">
                  {builderNotes[index]}
                </p>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          <motion.div {...reveal(0.14)} className="mt-8">
            <button
              type="button"
              onClick={() => {
                setIndex((i) => (i + 1) % builderNotes.length);
                stamp("notes");
              }}
              className="group inline-flex h-12 cursor-pointer items-center justify-center gap-2 rounded-full border-2 border-ink px-6 text-[0.95rem] font-semibold whitespace-nowrap text-ink transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:bg-ink/5 active:translate-y-0 active:scale-[0.98] motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100"
            >
              {notesCopy.another}
              <ArrowRight
                weight="bold"
                className="size-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:group-hover:translate-x-0"
                aria-hidden
              />
            </button>
          </motion.div>

          <motion.p {...reveal(0.2)} className="mt-14 text-center text-ink-soft">
            {notesCopy.outro}
          </motion.p>

          <motion.div {...reveal(0.26)} className="mt-6">
            <Button href="#boats" variant="primary">
              {notesCopy.cta}
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
